import CONFIG from '../config';
import { debug, isDebugEnabled } from './debug';

function clampNumber(value, minValue, maxValue, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(minValue, Math.min(maxValue, n));
}

export function getSearchProfile(profileKey = 'SEARCH_UI') {
  const profiles = CONFIG.SEARCH?.PROFILES || {};
  const normalizedKey = String(profileKey || 'SEARCH_UI').trim().toUpperCase();
  return profiles[normalizedKey] || profiles.SEARCH_UI || null;
}

export function getSearchStyle(styleKey = null) {
  const styles = CONFIG.SEARCH?.STYLES || {};
  const fallbackKey = String(CONFIG.SEARCH?.DEFAULT_DOC_SEARCH_STYLE || 'SEARCH_UI').trim().toUpperCase();
  const normalizedKey = String(styleKey || fallbackKey).trim().toUpperCase();
  return styles[normalizedKey] || styles[fallbackKey] || null;
}

export function normalizeSearchQuery(query) {
  return String(query || '').trim();
}

export function normalizeSearchLimit(limit, maxLimit = null) {
  const hardMaxLimit = clampNumber(CONFIG.SEARCH?.MAX_RESULT_LIMIT, 1, 5000, 1200);
  const configuredLimit = clampNumber(CONFIG.SEARCH?.RESULT_LIMIT, 1, 100, 20);
  const configuredMaxLimit = Number.isFinite(Number(maxLimit))
    ? clampNumber(maxLimit, 1, hardMaxLimit, configuredLimit)
    : configuredLimit;
  const requested = Number(limit);
  const effective = Number.isFinite(requested) ? requested : configuredMaxLimit;
  return clampNumber(effective, 1, configuredMaxLimit, configuredMaxLimit);
}

export function getSearchWeights(overrides = null) {
  const merged = {
    NAME_EXACT: 0,
    NAME_PREFIX: 0,
    NAME_TRGM: 0,
    MD_EXACT: 0,
    MD_TRGM: 0,
    MD_FTS: 0,
    HEADER_HIT: 0,
    ...(overrides || {})
  };

  return {
    name_exact: Number(merged.NAME_EXACT || 0),
    name_prefix: Number(merged.NAME_PREFIX || 0),
    name_trgm: Number(merged.NAME_TRGM || 0),
    md_exact: Number(merged.MD_EXACT || 0),
    md_trgm: Number(merged.MD_TRGM || 0),
    md_fts: Number(merged.MD_FTS || 0),
    header_hit: Number(merged.HEADER_HIT || 0)
  };
}

export function normalizeSearchMode(value, fallback = 'max') {
  const normalized = String(value || fallback).trim().toLowerCase();
  return normalized === 'aggregate' ? 'aggregate' : 'max';
}

export function normalizeSearchResultType(value, fallback = 'docs') {
  const normalized = String(value || fallback).trim().toLowerCase();
  return normalized === 'chunks' ? 'chunks' : 'docs';
}

export function normalizeSearchOptions(options = {}) {
  const normalizedScope = String(options.scope || CONFIG.SEARCH?.DEFAULT_SCOPE || 'project').trim().toLowerCase();
  const profile = getSearchProfile(options.profile || 'SEARCH_UI');
  const profileWeights = profile?.WEIGHTS || {};
  const configuredDefaultResultType = profile?.RESULT_TYPE || 'docs';
  const configuredDefaultRankMode = profile?.RANK_MODE || 'max';
  const defaultReturnContent = Boolean(CONFIG.SEARCH?.RETURN_CONTENT);
  const defaultSnippetLength = clampNumber(CONFIG.SEARCH?.CONTENT_SNIPPET_LENGTH, 60, 4000, 260);
  const maxSnippetLength = clampNumber(CONFIG.SEARCH?.CONTENT_SNIPPET_MAX_LENGTH, 60, 8000, 1200);
  const snippetLength = clampNumber(options.contentLength ?? options.snippetLength ?? defaultSnippetLength, 60, maxSnippetLength, defaultSnippetLength);
  const defaultMatchTarget = clampNumber(CONFIG.SEARCH?.CONTENT_SNIPPET_MATCH_TARGET, 0.05, 0.95, 0.5);
  const snippetMatchTarget = clampNumber(options.contentMatchTarget ?? options.snippetMatchTarget ?? defaultMatchTarget, 0.05, 0.95, defaultMatchTarget);
  const defaultSnippetsPerResult = clampNumber(CONFIG.SEARCH?.CONTENT_SNIPPETS_PER_RESULT, 1, 10, 3);
  const snippetsPerResult = clampNumber(options.contentSnippetsPerResult ?? options.snippetsPerResult ?? defaultSnippetsPerResult, 1, 10, defaultSnippetsPerResult);
  const markerStart = String(options.matchMarkerStart ?? CONFIG.SEARCH?.MATCH_MARKER_START ?? '[[MATCH_START]]');
  const markerEnd = String(options.matchMarkerEnd ?? CONFIG.SEARCH?.MATCH_MARKER_END ?? '[[MATCH_END]]');

  const normalizedResultType = normalizeSearchResultType(options.resultType, configuredDefaultResultType);
  const normalizedRankMode = normalizeSearchMode(options.rankMode, configuredDefaultRankMode);
  const aggregateTopK = clampNumber(
    options.aggregateTopK ?? profile?.AGGREGATE_TOP_K,
    1,
    10,
    3
  );
  const minScore = Number(options?.frontendFilter?.minScore ?? options.minScore ?? profile?.MIN_SCORE ?? 0);

  const profileResultLimit = Number(profile?.RESULT_LIMIT ?? CONFIG.SEARCH?.RESULT_LIMIT);
  const requestedLimit = Number(options.limit);
  const allowLimitOverride = Boolean(options.allowLimitOverride);
  const resolvedLimitCap = allowLimitOverride
    ? (options.maxLimit ?? CONFIG.SEARCH?.MAX_RESULT_LIMIT ?? 1200)
    : profileResultLimit;

  return {
    profile: String(options.profile || 'SEARCH_UI').trim().toUpperCase(),
    scope: normalizedScope,
    resultType: normalizedResultType,
    rankMode: normalizedRankMode,
    limit: normalizeSearchLimit(
      options.limit ?? profile?.RESULT_LIMIT ?? CONFIG.SEARCH?.RESULT_LIMIT,
      Number.isFinite(requestedLimit)
        ? Math.max(profileResultLimit || 1, Number(resolvedLimitCap || profileResultLimit || 1))
        : (resolvedLimitCap ?? profile?.RESULT_LIMIT ?? CONFIG.SEARCH?.RESULT_LIMIT)
    ),
    aggregateTopK,
    weights: getSearchWeights({ ...(profileWeights || {}), ...(options.weights || {}) }),
    returnContent: options.returnContent === undefined ? defaultReturnContent : Boolean(options.returnContent),
    contentLength: snippetLength,
    contentMatchTarget: snippetMatchTarget,
    contentSnippetsPerResult: snippetsPerResult,
    matchMarkerStart: markerStart,
    matchMarkerEnd: markerEnd,
    frontendFiltering: options.frontendFiltering ?? Boolean(CONFIG.SEARCH?.ENABLE_FRONTEND_FILTERING),
    excludeLikelyChatDocs: Boolean(options.excludeLikelyChatDocs),
    frontendFilter: {
      minScore,
      nameOnly: Boolean(options?.frontendFilter?.nameOnly),
      nameMatch: String(options?.frontendFilter?.nameMatch || 'contains').trim().toLowerCase(),
      excludeDocIds: Array.isArray(options?.frontendFilter?.excludeDocIds)
        ? options.frontendFilter.excludeDocIds.map(v => String(v))
        : []
    }
  };
}

export function applyFrontendSearchFilters(results = [], query = '', options = {}) {
  if (!Array.isArray(results)) return [];

  const queryLower = String(query || '').toLowerCase();
  const minScore = Number(options?.frontendFilter?.minScore || 0);
  const nameOnly = Boolean(options?.frontendFilter?.nameOnly);
  const nameMatch = String(options?.frontendFilter?.nameMatch || 'contains').trim().toLowerCase();
  const excluded = new Set((options?.frontendFilter?.excludeDocIds || []).map(v => String(v)));

  return results.filter((row) => {
    if (excluded.has(String(row?.doc_id ?? ''))) return false;
    if (Number(row?.total_score || 0) < minScore) return false;

    if (nameOnly && queryLower) {
      const name = String(row?.doc_name || '').toLowerCase();
      if (nameMatch === 'exact' && name !== queryLower) return false;
      if (nameMatch === 'prefix' && !name.startsWith(queryLower)) return false;
      if (nameMatch !== 'exact' && nameMatch !== 'prefix' && !name.includes(queryLower)) return false;
    }

    return true;
  });
}

export function buildSearchPayload({ projectId, query, options, rawResults, filteredResults }) {
  return {
    meta: {
      projectId,
      query,
      profile: options.profile,
      scope: options.scope,
      resultType: options.resultType,
      rankMode: options.rankMode,
      aggregateTopK: options.aggregateTopK,
      limit: options.limit,
      totalRaw: Array.isArray(rawResults) ? rawResults.length : 0,
      totalFiltered: Array.isArray(filteredResults) ? filteredResults.length : 0,
      frontendFiltering: Boolean(options.frontendFiltering)
    },
    rawResults: Array.isArray(rawResults) ? rawResults : [],
    results: Array.isArray(filteredResults) ? filteredResults : []
  };
}

function formatScore(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0.00';
  return numeric.toFixed(2);
}

function formatBreakdown(scoreDetails = {}, options = {}) {
  const includeNameSignals = options.includeNameSignals !== false;
  const breakdown = {
    name_exact: Number(scoreDetails?.name_exact || 0),
    name_prefix: Number(scoreDetails?.name_prefix || 0),
    name_trgm: Number(scoreDetails?.name_trgm || 0),
    md_exact: Number(scoreDetails?.md_exact || 0),
    md_trgm: Number(scoreDetails?.md_trgm || 0),
    md_fts: Number(scoreDetails?.md_fts || 0),
    header_hit: Number(scoreDetails?.header_hit || 0)
  };

  const parts = [];
  if (includeNameSignals) {
    parts.push(
      `name_exact=${formatScore(breakdown.name_exact)}`,
      `name_prefix=${formatScore(breakdown.name_prefix)}`,
      `name_trgm=${formatScore(breakdown.name_trgm)}`
    );
  }

  parts.push(
    `md_exact=${formatScore(breakdown.md_exact)}`,
    `md_trgm=${formatScore(breakdown.md_trgm)}`,
    `md_fts=${formatScore(breakdown.md_fts)}`,
    `header_hit=${formatScore(breakdown.header_hit)}`
  );

  return parts.join(' ');
}

function formatWeights(weights = {}) {
  return [
    `name_exact=${formatScore(weights?.name_exact)}`,
    `name_prefix=${formatScore(weights?.name_prefix)}`,
    `name_trgm=${formatScore(weights?.name_trgm)}`,
    `md_exact=${formatScore(weights?.md_exact)}`,
    `md_trgm=${formatScore(weights?.md_trgm)}`,
    `md_fts=${formatScore(weights?.md_fts)}`,
    `header_hit=${formatScore(weights?.header_hit)}`
  ].join(' ');
}

export function getChunkRowsFromResult(result = {}) {
  const topChunksFromDetails = Array.isArray(result?.score_details?.top_chunks)
    ? result.score_details.top_chunks
    : [];

  if (topChunksFromDetails.length > 0) {
    return topChunksFromDetails.map((chunk) => ({
      chunk_hash: chunk?.chunk_hash || null,
      chunk_score: chunk?.chunk_score,
      score_details: chunk?.score_details || {}
    }));
  }

  if (Array.isArray(result?.chunks) && result.chunks.length > 0) {
    return result.chunks;
  }

  if (result?.chunk_hash) {
    return [{
      chunk_hash: result.chunk_hash,
      chunk_score: result.chunk_score,
      score_details: result.score_details
    }];
  }

  const hashes = Array.isArray(result?.top_chunk_hashes) ? result.top_chunk_hashes : [];
  return hashes.map((chunkHash) => ({ chunk_hash: chunkHash }));
}

export function extractChunkHashesFromResults(results = []) {
  const hashes = new Set();
  for (const result of Array.isArray(results) ? results : []) {
    const rows = getChunkRowsFromResult(result);
    for (const row of rows) {
      const hash = String(row?.chunk_hash || '').trim();
      if (hash) hashes.add(hash);
    }
  }
  return Array.from(hashes);
}

export function logSearchRankings({ projectId, query, options, rawResults = [], results = [] } = {}) {
  const debugEnabled = typeof isDebugEnabled === 'function' ? isDebugEnabled() : false;
  if (!debugEnabled) return;

  const normalizedResults = Array.isArray(results) ? results : [];
  const normalizedRawResults = Array.isArray(rawResults) ? rawResults : [];
  const normalizedOptions = options || {};

  debug(
    'apiSearchProject',
    { type: 'log' },
    `q="${String(query || '').replace(/"/g, "'")}" result_type=${normalizedOptions.resultType || 'docs'} rank_mode=${normalizedOptions.rankMode || 'max'} limit=${normalizedOptions.limit ?? 20} aggregate_top_k=${normalizedOptions.aggregateTopK ?? 3} min_score=${formatScore(normalizedOptions?.frontendFilter?.minScore || 0)} raw=${normalizedRawResults.length} filtered=${normalizedResults.length} ${formatWeights(normalizedOptions.weights || {})}`
  );

  const maxRowsToLog = Math.max(1, Math.min(25, Number(CONFIG.SEARCH?.DEBUG_MAX_LOG_ROWS || 10)));
  const rowsToLog = normalizedResults.slice(0, maxRowsToLog);

  rowsToLog.forEach((result) => {
    const details = result?.score_details || {};
    const topChunkHash = String(
      result?.content_source_chunk_hash
      || result?.chunk_hash
      || getChunkRowsFromResult(result)?.[0]?.chunk_hash
      || ''
    ).trim() || 'null';
    const docLine = [
      `${String(result?.doc_name || '').replace(/\s+/g, ' ').trim() || 'Untitled'}`,
      `doc_id=${result?.doc_id ?? 'null'}`,
      `chunk_hash=${topChunkHash}`,
      `score=${formatScore(result?.total_score)}`,
      formatBreakdown(details)
    ].join(' ');

    console.groupCollapsed(`%c[apiSearchProject] ${docLine}`, 'font-weight: normal;');

    const chunkRows = getChunkRowsFromResult(result);
    chunkRows.forEach((chunk) => {
      const chunkDetails = chunk?.score_details || {};
      const chunkLine = [
        `${chunk?.chunk_hash || 'null'}`,
        `score=${formatScore(chunk?.chunk_score)}`,
        formatBreakdown(chunkDetails, { includeNameSignals: false })
      ].join(' ');

      debug('apiSearchProject', { type: 'log' }, chunkLine);
    });

    console.groupEnd();
  });

  const suppressedRows = normalizedResults.length - rowsToLog.length;
  if (suppressedRows > 0) {
    debug('apiSearchProject', { type: 'log' }, `suppressed_rows=${suppressedRows} (set SEARCH.DEBUG_MAX_LOG_ROWS to adjust)`);
  }
}

// ----------------- Name search utilities -----------------
function makeTrigrams(s) {
  const str = String(s || '').toLowerCase();
  const chars = [];
  for (let i = 0; i < str.length - 2; i += 1) {
    chars.push(str.substring(i, i + 3));
  }
  return new Set(chars.filter(Boolean));
}

function trigramSimilarity(a = '', b = '') {
  if (!a || !b) return 0;
  const sa = makeTrigrams(a);
  const sb = makeTrigrams(b);
  if (sa.size === 0 || sb.size === 0) return 0;
  let intersection = 0;
  sa.forEach(t => { if (sb.has(t)) intersection += 1; });
  return (2 * intersection) / (sa.size + sb.size);
}

/**
 * Search an array of items by a `name`-like key using precedence:
 * 1) exact match
 * 2) startsWith
 * 3) contains
 * 4) fuzzy trigram score
 */
export function searchByName(items = [], query = '', options = {}) {
  if (!Array.isArray(items) || !query) return [];
  const cfg = (options?.config) || (CONFIG.SEARCH && CONFIG.SEARCH.NAME_SEARCH) || {};
  const key = options?.key || 'name';
  const limit = Number(options?.limit || cfg.RESULT_LIMIT || 20) || 20;
  const fuzzyThreshold = Number(options?.fuzzyThreshold ?? cfg.FUZZY_THRESHOLD ?? 0.25);

  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];

  const exact = [];
  const prefix = [];
  const contains = [];
  const fuzzy = [];

  for (const it of items) {
    const name = String(it?.[key] || '').trim();
    const nameLower = name.toLowerCase();
    if (!name) continue;
    if (nameLower === q) {
      exact.push(it);
      continue;
    }
    if (nameLower.startsWith(q)) {
      prefix.push(it);
      continue;
    }
    if (nameLower.includes(q)) {
      contains.push(it);
      continue;
    }
    const score = trigramSimilarity(nameLower, q);
    if (score >= fuzzyThreshold) {
      fuzzy.push({ item: it, score });
    }
  }

  // sort fuzzy by score desc then name
  fuzzy.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const na = String(a.item?.[key] || '').toLowerCase();
    const nb = String(b.item?.[key] || '').toLowerCase();
    return na.localeCompare(nb);
  });

  const results = [
    ...exact,
    ...prefix,
    ...contains,
    ...fuzzy.map(f => f.item)
  ].slice(0, limit);

  return results;
}
